"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Typography } from "@/components/typography"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { trackEvent } from "@/lib/attribution"

interface ContactFormProps {
  onSubmit: (contactInfo: { name: string; email: string; company?: string }) => void
  onBack: () => void
  isSubmitting?: boolean
  submitStatus?: "idle" | "success" | "error"
}

export function ContactForm({ onSubmit, onBack, isSubmitting = false, submitStatus = "idle" }: ContactFormProps) {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [company, setCompany] = useState("")
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({})

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    const newErrors: { name?: string; email?: string } = {}
    if (!name.trim()) newErrors.name = "Name is required"
    if (!email.trim()) newErrors.email = "Email is required"
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email"
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      trackEvent("form_error", {
        form_id: "contact_configurator",
        fields: Object.keys(newErrors).join(","),
      })
      return
    }

    onSubmit({ name: name.trim(), email: email.trim(), company: company.trim() || undefined })
  }

  return (
    <div className="space-y-8">
      <div>
        <Typography variant="h2" as="h2" className="mb-4">
          Your Contact Information
        </Typography>
        <Typography variant="body" className="text-tk-slate/80">
          Let&apos;s connect. I&apos;ll review your project details and get back to you soon.
        </Typography>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium text-tk-onyx font-ui">
            Name <span className="text-tk-teal">*</span>
          </Label>
          <Input
            id="name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (errors.name) setErrors({ ...errors, name: undefined })
            }}
            className={`border-tk-slate/20 bg-tk-linen ${
              errors.name ? "border-tk-teal" : ""
            }`}
            placeholder="Your name"
          />
          {errors.name && (
            <p className="text-xs text-tk-teal font-body">{errors.name}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-tk-onyx font-ui">
            Email <span className="text-tk-teal">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (errors.email) setErrors({ ...errors, email: undefined })
            }}
            className={`border-tk-slate/20 bg-tk-linen ${
              errors.email ? "border-tk-teal" : ""
            }`}
            placeholder="your@email.com"
          />
          {errors.email && (
            <p className="text-xs text-tk-teal font-body">{errors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="company" className="text-sm font-medium text-tk-onyx font-ui">
            Company <span className="text-tk-slate/70 text-xs">(Optional)</span>
          </Label>
          <Input
            id="company"
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="border-tk-slate/20 bg-tk-linen"
            placeholder="Your company name"
          />
        </div>

        <div className="flex justify-between pt-6">
          <Button
            type="button"
            onClick={onBack}
            variant="outline"
            className="rounded-full border-tk-slate/25 bg-tk-linen text-sm text-tk-slate hover:border-tk-teal hover:text-tk-onyx font-ui"
          >
            ← Back
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="rounded-full bg-tk-teal px-8 py-2 text-sm font-semibold text-tk-linen hover:bg-tk-teal/90 font-ui disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Sending..." : "Submit Project Request"}
          </Button>
        </div>
      </form>

      {submitStatus === "success" && (
        <div className="mt-6 p-4 rounded-lg bg-tk-teal/10 border border-tk-teal/20">
          <Typography variant="body" className="text-tk-teal font-semibold">
            ✓ Thank you! Your project details have been submitted. I&apos;ll be in touch soon.
          </Typography>
        </div>
      )}

                {submitStatus === "error" && (
                  <div className="mt-6 p-4 rounded-lg bg-tk-teal/10 border border-tk-teal/20">
                    <Typography variant="body" className="text-tk-teal font-semibold">
                      Something went wrong. Please try again,{" "}
                      <a href="mailto:hello@tallkarol.com?subject=Tall%20Karol%20Website%20Contact%20Request" className="underline hover:text-tk-teal">
                        email me directly
                      </a>
                      , or{" "}
                      <a href="tel:+12165058192" className="underline hover:text-tk-teal">
                        give me a call
                      </a>
                      .
                    </Typography>
                  </div>
                )}
    </div>
  )
}

